import * as _ from '@/index'
import { AppAuth } from '../auth'
import { toQueryString } from '../function'
import axios from 'axios'
import type {
  DeviceEventLog,
  AppVersion,
  TaskDataLength,
  ExcelColumnMappingResult,
  UserDefectInputItem,
  DefectItemDefinition,
  DefectItemRef,
  DefectItemKey,
  DefectItemImportStats,
  DefectItemEditable,
  UserSettingConfig,
  ExportTemplate,
  UserDefectInputPreset,
  ShapeComment,
  ShapeCommentPhoto,
  UserExportReportRecord,
  AddUserExportReportRecord,
  UserExportReport,
  UserInfo,
  CompanyInfo,
  CompanyCategory,
  UserRole,
  ReportImport,
  ReportImportDetail,
  ReportImportCommitResult,
} from './type'
import type {
  PublicData,
  Facility,
  Task,
  Floorplan,
  FacilityPhoto,
  Defect,
  DefectPhoto,
  DefectDetailItem,
  VoiceFile,
  QualityChecklistSet,
  QualityChecklistZone,
  QualityChecklistItem,
  QualityTaskAssignment,
  QualityInspection,
  QualityInspectionResult,
  QualityResultPhoto,
  QualityPartner,
  QualityInspectionStatus,
} from './types'
import { API_URL } from './enum/api'
import { DwgData } from '../type/cad'
import type { Qna, QnaComment, QnaFile } from './qna'
import type { Announcement, AnnouncementPhoto } from './announcement'

// 묶음 수정 응답의 검증 거절 항목
export type DefectRejectedItem = { index: number; id: number | null; message: string }

// 거절 결함 처리 기록 (서버 defect_sync_rejections, source='app')
export type DefectSyncRejectionLog = {
  action: 'deleted' | 'reverted'
  payload: Record<string, unknown>
  related?: Record<string, unknown> | null
  task_id?: string | number
  floorplan_id?: string | number
  defect_id?: string | number
  local_string_id?: string
  message?: string
}

/* 공통 사항
 * get할 때, lenght가 1인 배열로 return됨
 *
 */

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace APIServer {
  // addTask, task 추가 및 task_assets, sample_floors 추가
  // facility 추가는 없음,
  // taskId return
  export const addTask = async (task: Partial<Task>[]): Promise<Partial<Task>[]> => {
    // task insertId return
    return await AppAuth.Post<Partial<Task>[]>(`${API_URL}tasks`, task)
  }

  /**
   * 과업 상세 조회 - 단일 배열 [Task] return
   * @param id - task의 id (number string)
   * @param options.excludeDefects - true면 중첩 defects/defect_photos 제외 (앱은 플랫 API로 별도 수신)
   */
  export const getTask = async (
    id: string,
    options?: { excludeDefects?: boolean },
  ): Promise<Task[]> => {
    const exclude = options?.excludeDefects ? '&exclude_defects=true' : ''
    return await AppAuth.Get<Task[]>(`${API_URL}tasks/detail?id=${id}${exclude}`)
  }

  export const getTaskList = async (): Promise<Task[]> => {
    return await AppAuth.Get<Task[]>(`${API_URL}tasks`)
  }

  /**
   * 관련 과업 체인 조회 (재귀적으로 연결된 모든 과업 포함)
   * 특정 과업과 이전/이후 연결된 모든 과업들을 조회 (previous, current, next)
   */
  export const getTaskRelationChain = async (id: string): Promise<Task[]> => {
    return await AppAuth.Get<Task[]>(`${API_URL}tasks/relation-chain?id=${id}`)
  }

  // Task만 update(task_assets, sample_floors 무시, facility 무시)
  export const updateTaskOnly = async (task: Partial<Task[]>): Promise<void> => {
    // no return
    return await AppAuth.Put(`${API_URL}tasks`, task)
  }

  export const updateTaskAttributes = async (task: Partial<Task>[]): Promise<void> => {
    // no return
    return await AppAuth.Put(`${API_URL}tasks`, task)
  }

  // deleteTask, id는 number string
  // task 삭제 및 task_assets, sample_floors 소프트 삭제
  // facility는 삭제되지 않음
  // id는 task의 id
  export const deleteTask = async (ids: { id: number }[]): Promise<void> => {
    // no return
    return await AppAuth.Delete(`${API_URL}tasks`, ids)
  }

  // 삭제된 과업 목록 조회 (삭제 후 1개월 이내 건만 서버에서 반환)
  export const getDeletedTaskList = async (): Promise<Task[]> => {
    return await AppAuth.Get<Task[]>(`${API_URL}tasks/deleted`)
  }

  // 삭제된 과업 복원
  export const restoreTask = async (ids: { id: number }[]): Promise<{ successCount: number }> => {
    return await AppAuth.Post<{ successCount: number }>(`${API_URL}tasks/restore`, ids)
  }

  // Facility 추가, facilityId return
  export const addFacility = async (newFacility: Partial<Facility>[]): Promise<Facility[]> => {
    // facility insertId return
    return await AppAuth.Post<Facility[]>(`${API_URL}facilities`, newFacility)
  }

  // FacilityBranch 수정
  export const updateFacilityBranch = async (
    facilityBranches: Partial<Facility>[],
  ): Promise<void> => {
    // no return
    return await AppAuth.Put(`${API_URL}facilities`, facilityBranches)
  }

  // FacilityBranch 삭제 및 해당 facility_branch_id를 갖는 floorplan 배열도 소프트 삭제
  // id는 facilityBranch의 id
  export const deleteFacilityBranch = async (ids: { id: number }[]): Promise<void> => {
    // no return
    return await AppAuth.Delete(`${API_URL}facilities`, ids)
  }

  // Floorplan 관련 함수
  // (add, get, update, delete)Floorplan은 facility_branch_id와 별개로 수정
  // (add, get, update, delete)FloorplanWithBranch는 facility_branch_id를 통해 수정
  // 정리하면,
  // 최신 floorplan에 대한 CRUD는 (add, get, update, delete)Floorplan
  // branch된 floorplan에 대한 CRUD는 (add, get, update, delete)FloorplanWithBranch
  // Floorplan 추가
  // 파일에 대한 업스케일이 이루어짐으로 결과행을 반환함
  export const addFloorplans = async (floorplans: Partial<Floorplan>[]): Promise<Floorplan[]> => {
    // floorplan insertId return
    return await AppAuth.Post<Floorplan[]>(`${API_URL}floorplans`, floorplans)
  }

  export const cloneFloorplans = async (floorplans: Partial<Floorplan>[]): Promise<Floorplan[]> => {
    return await AppAuth.Post<Floorplan[]>(`${API_URL}floorplans/clone`, floorplans)
  }

  export const getFloorplanByTaskId = async (taskId: string): Promise<Floorplan[]> => {
    return await AppAuth.Get<Floorplan[]>(`${API_URL}floorplans?task_id=${taskId}`)
  }

  // Floorplan 조회
  // single array인 [Floorplan] return
  // id는 floorplan의 id
  export const getFloorplan = async (floorplanId: string): Promise<Floorplan[]> => {
    return await AppAuth.Get<Floorplan[]>(`${API_URL}floorplans?id=${floorplanId}`)
  }

  // Floorplan 수정
  // 파일에 대한 업스케일이 이루어짐으로 결과행을 반환함
  export const updateFloorplan = async (floorplans: Partial<Floorplan>[]): Promise<Floorplan[]> => {
    // no return
    return await AppAuth.Put(`${API_URL}floorplans`, floorplans)
  }

  // Floorplan 삭제
  // id는 floorplan의 id
  export const deleteFloorplans = async (ids: { id: number }[]): Promise<void> => {
    // no return
    return await AppAuth.Delete(`${API_URL}floorplans`, ids)
  }

  // shape only
  // shapeId return
  export const addShape = async (shapes: Partial<Defect>[]): Promise<Defect[]> => {
    // shape insertId return
    return await AppAuth.Post<Defect[]>(`${API_URL}defects`, shapes)
  }

  // shape only
  // signle array인 [Shape] return
  export const getShape = async (id: string): Promise<Defect[]> => {
    return await AppAuth.Get<Defect[]>(`${API_URL}defects?id=${id}`)
  }

  // 여러 id를 한 번에 조회 (서버는 id=[1,2,3] 배열 파라미터를 = ANY 조건으로 처리)
  export const getShapes = async (ids: (string | number)[]): Promise<Defect[]> => {
    if (ids.length === 0) return []
    const param = encodeURIComponent(JSON.stringify(ids.map(id => Number(id))))
    return await AppAuth.Get<Defect[]>(`${API_URL}defects?id=${param}`)
  }

  // shape only
  export const updateShapes = async (shapes: Partial<Defect>[]): Promise<Defect[]> => {
    return await AppAuth.Put<Defect[]>(`${API_URL}defects`, shapes)
  }

  // 묶음 수정 — 검증에서 거절된 항목(id·사유)도 함께 돌려준다 (서버는 나머지만 반영하고 200으로 응답)
  export const updateShapesWithRejected = async (
    shapes: Partial<Defect>[],
  ): Promise<{ rows: Defect[]; rejected: DefectRejectedItem[] }> => {
    let rejected: DefectRejectedItem[] = []
    const rows = await AppAuth.Put<Defect[]>(`${API_URL}defects`, shapes, {
      onRejected: items => {
        rejected = items as DefectRejectedItem[]
      },
    })
    return { rows: Array.isArray(rows) ? rows : [], rejected }
  }

  // 거절 결함을 기기에서 삭제·되돌리기 전 내용 기록
  export const addDefectSyncRejections = async (items: DefectSyncRejectionLog[]): Promise<void> => {
    return await AppAuth.Post(`${API_URL}defects/sync-rejections`, items, { timeoutMs: 15000 })
  }

  // shape only
  // id는 shape Id
  export const deleteShape = async (ids: { id: number }[]): Promise<void> => {
    // no return
    return await AppAuth.Delete(`${API_URL}defects`, ids)
  }

  export const restoreShape = async (ids: { id: number }[]): Promise<void> => {
    return await AppAuth.Post(`${API_URL}defects/restore`, ids)
  }

  // defect photo only
  // defectPhotoId return
  export const addDefectPhoto = async (form: Partial<DefectPhoto>[]): Promise<DefectPhoto[]> => {
    // defectPhoto insertId return
    return await AppAuth.Post<DefectPhoto[]>(`${API_URL}defect-photos`, form)
  }

  // defect photo only
  export const updateDefectPhoto = async (
    photo: Partial<DefectPhoto>[],
  ): Promise<DefectPhoto[]> => {
    return await AppAuth.Put<DefectPhoto[]>(`${API_URL}defect-photos`, photo)
  }

  // defect photo only
  // id는 defect photo의 id
  export const deleteDefectPhoto = async (ids: { id: number }[]): Promise<void> => {
    // no return
    return await AppAuth.Delete(`${API_URL}defect-photos`, ids)
  }

  export const restoreDefectPhoto = async (ids: { id: number }[]): Promise<DefectPhoto[]> => {
    return await AppAuth.Post<DefectPhoto[]>(`${API_URL}defect-photos/restore`, ids)
  }

  // Additional API Added
  export const addFacilityBranchPhoto = async (
    form: Partial<FacilityPhoto>[],
  ): Promise<FacilityPhoto[]> => {
    return await AppAuth.Post<FacilityPhoto[]>(`${API_URL}facility-photos`, form)
  }

  export const updateFacilityBranchPhoto = async (
    photos: Partial<FacilityPhoto>[],
  ): Promise<FacilityPhoto[]> => {
    return await AppAuth.Put(`${API_URL}facility-photos`, photos)
  }

  export const deleteFacilityBranchPhoto = async (ids: { id: number }[]): Promise<void> => {
    return await AppAuth.Delete(`${API_URL}facility-photos`, ids)
  }

  // get FacilityBranchPhoto list By TaskId
  export const getFacilityBranchPhotoListByTaskId = async (
    taskId: string,
  ): Promise<FacilityPhoto[]> => {
    return await AppAuth.Get<FacilityPhoto[]>(`${API_URL}facility-photos?task_id=${taskId}`)
  }

  export const getDefectPhotoListByTaskId = async (id: string): Promise<DefectPhoto[]> => {
    return await AppAuth.Get<DefectPhoto[]>(`${API_URL}defect-photos?task_id=${id}`)
  }

  /**
   * DwgData로 캐드파일을 생성하고 다운로드 url을 반환
   * @param data.dwgData - 도면에 포함될 레이어 및 도형 정보
   * @param data.taskId - 과업 id (파일 경로 생성용)
   * @param data.floorIds - 층 id 목록 (파일 경로 생성용)
   *
   * @returns.success - 요청 처리 성공 여부
   * @returns.file.url - 생성된 DWG 파일의 다운로드 URL (GCP Storage 경로)
   * @returns.file.size - 생성된 DWG 파일의 크기 (bytes)
   */
  export const exportDWG = async (data: {
    dwgData: DwgData
    path: string
  }): Promise<{
    success: boolean
    file: {
      url: string
      size: number
    }
  }> => {
    return await AppAuth.Post<{
      success: boolean
      file: {
        url: string
        size: number
      }
    }>(`${API_URL}cad/export-dwg`, data)
  }

  // get Latest Query Logs
  // return QueryLog[],
  // created_at desc limit 10

  export const getShapeListByTaskId = async (id: number): Promise<Defect[]> => {
    return await AppAuth.Get<Defect[]>(`${API_URL}defects?task_id=${id}`)
  }

  // 엑셀 컬럼 AI 매핑 (전차 결함 스마트 불러오기)
  export async function analyzeExcelColumnMapping(payload: {
    headerCandidates: string[][]
    sampleRows: string[][]
  }): Promise<ExcelColumnMappingResult> {
    // AI 응답 지연 시 로딩이 무한 유지되지 않도록 타임아웃 지정 (실패 시 수동 매핑 폴백)
    return await AppAuth.Post<ExcelColumnMappingResult>(
      `${API_URL}ai/excel-column-mapping`,
      payload,
      { timeoutMs: 30_000 },
    )
  }

  // 입력항목 설정 - 시작
  // Read
  export async function getUserDefectInputItemList(
    where?: Partial<
      Pick<
        UserDefectInputItem,
        'structure' | 'type' | 'member' | 'defect' | 'id' | 'building_type' | 'inspection_period'
      >
    >,
  ): Promise<UserDefectInputItem[]> {
    const queryString = where ? toQueryString(where) : ''
    return await AppAuth.Get<UserDefectInputItem[]>(
      `${API_URL}company-defect-input-items${queryString}`,
    )
  }

  // Update
  export async function updateUserDefectInputItemValues(
    data: Array<{
      id: number
      values: string | UserDefectInputItem['values']
      fields_updated_at?: Record<string, string>
    }>,
  ): Promise<number> {
    return await AppAuth.Put<number>(`${API_URL}company-defect-input-items`, data)
  }

  // 하자항목 정의 (하자진단 부록 문구) - 위치 배치는 서버가 같은 트랜잭션으로 함께 바꿈
  export async function getDefectItems(
    where?: Partial<DefectItemKey> & { id?: number },
  ): Promise<DefectItemDefinition[]> {
    const queryString = where ? toQueryString(where) : ''
    return await AppAuth.Get<DefectItemDefinition[]>(`${API_URL}company-defect-items${queryString}`)
  }

  // 같은 이름이 있으면 그 정의를 재사용하고 members 위치에 배치만 추가 (전유 POST 공종은 members 무시)
  export async function addDefectItems(
    data: Array<DefectItemKey & DefectItemEditable & { name: string; members?: string[] }>,
  ): Promise<DefectItemDefinition[]> {
    return await AppAuth.Post<DefectItemDefinition[]>(`${API_URL}company-defect-items`, data)
  }

  // name 변경 시 위치 row values(+공종 자식 row)까지 함께 변경
  export async function updateDefectItems(
    data: Array<DefectItemEditable & { id: number; name?: string }>,
  ): Promise<DefectItemDefinition[]> {
    return await AppAuth.Put<DefectItemDefinition[]>(`${API_URL}company-defect-items`, data)
  }

  // members 위치에서만 제거(없으면 전부). 남은 배치가 없으면 정의 삭제
  export async function deleteDefectItems(
    data: Array<{ id: number; members?: string[] }>,
  ): Promise<{ deleted_ids: number[]; kept_ids: number[] }> {
    return await AppAuth.Delete<{ deleted_ids: number[]; kept_ids: number[] }>(
      `${API_URL}company-defect-items`,
      data,
    )
  }

  // 엑셀 가져오기: 새 근거 + 시트(구분·시기)별 배치·정의를 한 트랜잭션으로 (merge = 덧붙이기, replace = 엑셀로 교체)
  export async function importDefectItems(data: {
    refs: Array<
      { key: string } & Partial<
        Pick<DefectItemRef, 'text' | 'image_url' | 'image_width' | 'image_height'>
      >
    >
    sheets: unknown[]
  }): Promise<DefectItemImportStats> {
    return await AppAuth.Post<DefectItemImportStats>(`${API_URL}company-defect-items/import`, data)
  }

  // 구분·시기 하나의 위치·하자항목·상세 전부 삭제 (근거 라이브러리는 유지)
  export async function clearDefectItems(key: DefectItemKey): Promise<DefectItemImportStats> {
    return await AppAuth.Post<DefectItemImportStats>(`${API_URL}company-defect-items/clear`, key)
  }

  export async function getDefectItemRefs(where?: { id?: number }): Promise<DefectItemRef[]> {
    const queryString = where ? toQueryString(where) : ''
    return await AppAuth.Get<DefectItemRef[]>(`${API_URL}company-defect-refs${queryString}`)
  }

  export async function getDefectItemRefUsage(
    id: number,
  ): Promise<Pick<DefectItemDefinition, 'id' | 'structure' | 'inspection_period' | 'name'>[]> {
    return await AppAuth.Get(`${API_URL}company-defect-refs/usage?id=${id}`)
  }

  export async function addDefectItemRefs(
    data: Array<
      Partial<Pick<DefectItemRef, 'text' | 'image_url' | 'image_width' | 'image_height'>>
    >,
  ): Promise<DefectItemRef[]> {
    return await AppAuth.Post<DefectItemRef[]>(`${API_URL}company-defect-refs`, data)
  }

  export async function updateDefectItemRefs(
    data: Array<
      { id: number } & Partial<
        Pick<DefectItemRef, 'text' | 'image_url' | 'image_width' | 'image_height'>
      >
    >,
  ): Promise<DefectItemRef[]> {
    return await AppAuth.Put<DefectItemRef[]>(`${API_URL}company-defect-refs`, data)
  }

  // 쓰는 하자항목이 있으면 서버가 409로 거절
  export async function deleteDefectItemRefs(ids: number[]): Promise<{ successCount: number }> {
    return await AppAuth.Delete<{ successCount: number }>(
      `${API_URL}company-defect-refs`,
      ids.map(id => ({ id })),
    )
  }
  // 입력항목 설정 - 끝

  export const addDeviceEventLog = async (eventLog: Partial<DeviceEventLog[]>): Promise<void> => {
    return await AppAuth.Post(`${API_URL}device-event-logs`, eventLog)
  }

  export const getDeviceEventLog = async (taskId: string): Promise<DeviceEventLog[]> => {
    return await AppAuth.Get<DeviceEventLog[]>(`${API_URL}device-event-logs?task_id=${taskId}`)
  }

  export const useTaskDataAsPrevious = async (
    sourceId: number,
    targetId: number,
    includeBrush: boolean = false,
  ): Promise<void> => {
    return await AppAuth.Post(`${API_URL}tasks/copy-as-previous`, {
      source_id: sourceId,
      target_id: targetId,
      include_brush: includeBrush,
    })
  }

  export const zipDefectPhoto = async (data: {
    condition: string // floorplan, facility_branch
    id: number
    zip_name: string
    shape_map: {
      [key: number]: number
    }
  }): Promise<void> => {
    return await AppAuth.Post(`${API_URL}zipDefectPhoto`, data)
  }

  // 해당 파일명을 가진 task_asset을 조회하여 번호가 있는 파일명으로 반환
  // 예: 'example.jpg'가 이미있음 -> 'example (2).jpg'
  export const getTaskAssetFileNameByContent = async (fileName: string): Promise<string> => {
    const params = new URLSearchParams({ content: fileName })
    return await AppAuth.Get<string>(`${API_URL}getTaskAssetFileNameByContent?${params.toString()}`)
  }

  // 사용자의 출력 양식 목록을 가져옴
  // company가 없는 행은 공통 양식임
  export const getUserExportTemplateList = async (): Promise<ExportTemplate[]> => {
    return await AppAuth.Get<ExportTemplate[]>(`${API_URL}company-export-templates`)
  }

  export const getTaskDataLength = async (taskId: string): Promise<TaskDataLength[]> => {
    return await AppAuth.Get<TaskDataLength[]>(`${API_URL}tasks/data-count?task_id=${taskId}`)
  }

  export const getFacilityDataLength = async (facilityIds: number[]): Promise<TaskDataLength[]> => {
    return await AppAuth.Get<TaskDataLength[]>(
      `${API_URL}facilities/data-count?ids=${JSON.stringify(facilityIds)}`,
    )
  }

  // 개인 설정 조회
  export const getUserSetting = async (): Promise<{
    config: UserSettingConfig[]
    testMeasureForm: DefectDetailItem[]
  }> => {
    const result = await AppAuth.Get<{
      config: UserSettingConfig[]
      testMeasureForm: DefectDetailItem[]
    }>(`${API_URL}company-settings`)
    return result
  }

  // 개인 설정 업데이트
  // key는 해당 값을 찾기위한 키임
  // 예: { key: "defect.defaultDefectValue.STRUCTURE", value: "SS" }
  export const updateUserSettingConfig = async (data: {
    key: string
    value: UserSettingConfig['settings'][number]['value']
  }): Promise<number> => {
    return await AppAuth.Put<number>(`${API_URL}company-settings/config`, data)
  }
  // 입력항목 프리셋 시작
  // 조회
  export const getUserDefectInputPresetList = async (): Promise<UserDefectInputPreset[]> => {
    return await AppAuth.Get<UserDefectInputPreset[]>(`${API_URL}company-defect-input-presets`)
  }

  // 추가
  export const addUserDefectInputPreset = async (
    data: Partial<UserDefectInputPreset>[],
  ): Promise<number> => {
    const { id } = await AppAuth.Post<{ id: number }>(
      `${API_URL}company-defect-input-presets`,
      data,
    )
    return id
  }

  // 업데이트
  export const updateUserDefectInputPreset = async (
    data: Partial<UserDefectInputPreset>[],
  ): Promise<number> => {
    return await AppAuth.Put<number>(`${API_URL}company-defect-input-presets`, data)
  }

  // 삭제
  export const deleteUserDefectInputPreset = async (ids: { id: number }[]): Promise<void> => {
    await AppAuth.Delete<string>(`${API_URL}company-defect-input-presets`, ids)
  }

  export const generatePublicLink = async (
    data: {
      facility_ids: number[]
      floorplan_ids: number[]
      defect_ids: number[]
    }[],
  ): Promise<string> => {
    const { key } = await AppAuth.Post<{ key: string }>(`${API_URL}public-link`, data)
    return key
  }

  export const getShapeCommentsByFloorplanId = async (
    floorplanId: number,
  ): Promise<ShapeComment[]> => {
    return await AppAuth.Get<ShapeComment[]>(
      `${API_URL}defect-comments?floorplan_id=${floorplanId}`,
    )
  }

  export const getShapeCommentPhotosByFloorplanId = async (
    floorplanId: number,
  ): Promise<ShapeCommentPhoto[]> => {
    return await AppAuth.Get<ShapeCommentPhoto[]>(
      `${API_URL}defect-comment-photos?floorplan_id=${floorplanId}`,
    )
  }

  export const getShapeCommentsByShapeId = async (defectId: number): Promise<ShapeComment[]> => {
    return await AppAuth.Get<ShapeComment[]>(`${API_URL}defect-comments?defect_id=${defectId}`)
  }

  export const getShapeCommentPhotosByShapeId = async (
    defectId: number,
  ): Promise<ShapeCommentPhoto[]> => {
    return await AppAuth.Get<ShapeCommentPhoto[]>(
      `${API_URL}defect-comment-photos?defect_id=${defectId}`,
    )
  }

  export const getUserExportReportRecordList = async (
    taskId: number,
  ): Promise<UserExportReportRecord[]> => {
    return await AppAuth.Get<UserExportReportRecord[]>(
      `${API_URL}company-export-report-records?task_id=${taskId}`,
    )
  }

  export const getUserExportReportRecordListCurrent = async (
    taskId: number,
    facilityId: number,
    templateId: number,
  ): Promise<UserExportReportRecord[]> => {
    return await AppAuth.Get<UserExportReportRecord[]>(
      `${API_URL}company-export-report-records?task_id=${taskId}&facility_id=${facilityId}&template_id=${templateId}`,
    )
  }

  export const addUserExportReportRecord = async (
    data: AddUserExportReportRecord[],
  ): Promise<UserExportReportRecord[]> => {
    return await AppAuth.Post<UserExportReportRecord[]>(
      `${API_URL}company-export-report-records`,
      data,
    )
  }

  export const updateUserExportReportRecord = async (
    data: AddUserExportReportRecord[],
  ): Promise<UserExportReportRecord[]> => {
    return await AppAuth.Put(`${API_URL}company-export-report-records`, data)
  }

  export const getUserExportReportList = async (): Promise<UserExportReport[]> => {
    return await AppAuth.Get<UserExportReport[]>(`${API_URL}company-export-reports`)
  }

  export const updateUserExportReport = async (
    data: Partial<UserExportReport>[],
  ): Promise<Partial<UserExportReport>[]> => {
    return await AppAuth.Put(`${API_URL}company-export-reports`, data)
  }

  export const deleteUserExportReport = async (id: number): Promise<void> => {
    return await AppAuth.Delete(`${API_URL}company-export-reports?id=${id}`)
  }

  // 음성 파일 추가
  export const addVoiceFiles = async (form: Partial<VoiceFile>[]): Promise<VoiceFile[]> => {
    return await AppAuth.Post<VoiceFile[]>(`${API_URL}voice-files`, form)
  }

  // 음성 파일 목록 호출
  export const getVoiceFileList = async (
    params: {
      task_id?: number
      facility_id?: number
      floorplan_id?: number
      defect_id?: number
      template_id?: number
      record_item_key?: string
      order?: string
    } = {},
  ): Promise<VoiceFile[]> => {
    // 값이 있는 것만 추려서 쿼리스트링 생성
    const query = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => v != null && query.append(k, String(v)))

    const queryString = query.toString()
    return await AppAuth.Get<VoiceFile[]>(
      `${API_URL}voice-files${queryString ? `?${queryString}` : ''}`,
    )
  }

  // 음성 파일 삭제
  export const deleteVoiceFiles = async (ids: { id: number }[]): Promise<void> => {
    return await AppAuth.Delete(`${API_URL}voice-files`, ids)
  }

  // 내정보 가져오기 (회색지대 네트워크에서 무한 대기하지 않도록 타임아웃 적용)
  export const getMe = async (): Promise<UserInfo> => {
    const { user } = (await AppAuth.Get(`${API_URL}me`, { timeoutMs: 10_000 })) as {
      user: UserInfo
    }
    return user
  }

  // 회사 목록 조회
  export const getCompanyList = async (type: 'BIZ' | 'IND'): Promise<Array<CompanyInfo>> => {
    return await AppAuth.Get<Array<CompanyInfo>>(`${API_URL}admin/companies?type=${type}`)
  }

  // 회사 추가
  export const addCompany = async (data: { name: string }): Promise<Array<CompanyInfo>> => {
    return await AppAuth.Post<Array<CompanyInfo>>(`${API_URL}admin/companies`, [
      { name: data.name, type: 'BIZ' },
    ])
  }

  // 회사와 사용자 동시 추가
  export const addCompanyWithUser = async (data: {
    companyName: string
    companyCategory?: CompanyCategory
    login_id: string
    userName?: string
    password?: string
  }): Promise<Array<UserInfo>> => {
    return await AppAuth.Post<Array<UserInfo>>(`${API_URL}admin/company-users`, [
      {
        companyName: data.companyName,
        companyCategory: data.companyCategory,
        login_id: data.login_id,
        userName: data.userName,
        password: data.password,
      },
    ])
  }

  // 사용자 추가 (password가 있으면 사용, 없으면 서버에서 자동 생성)
  export const addUser = async (data: {
    login_id: string
    company_id: number
    name: string
    email?: string
    password?: string
  }): Promise<Array<UserInfo>> => {
    return await AppAuth.Post<Array<UserInfo>>(`${API_URL}admin/users`, [
      {
        login_id: data.login_id,
        company_id: data.company_id,
        name: data.name,
        email: data.email,
        password: data.password,
      },
    ])
  }

  // 회사의 사용자 목록 조회
  export const getCompanyUsers = async (companyId: number): Promise<Array<UserInfo>> => {
    return await AppAuth.Get<Array<UserInfo>>(`${API_URL}admin/users?company_id=${companyId}`)
  }

  // 일반 회원 목록 조회 (company.type = 'IND'인 사용자)
  export const getNormalUsers = async (): Promise<Array<UserInfo>> => {
    // IND 타입 회사 목록 조회
    const indCompanies = await AppAuth.Get<Array<CompanyInfo>>(`${API_URL}admin/companies?type=IND`)

    // IND 회사가 없으면 빈 배열 반환
    if (indCompanies.length === 0) return []

    // IND 회사의 모든 사용자 조회
    const companyIds = indCompanies.map(c => c.id)
    return await AppAuth.Get<Array<UserInfo>>(
      `${API_URL}admin/users?company_id=${JSON.stringify(companyIds)}`,
    )
  }

  // 회사명 수정
  export const updateCompanyName = async (
    companyId: number,
    name: string,
  ): Promise<Array<CompanyInfo>> => {
    return await AppAuth.Put<Array<CompanyInfo>>(`${API_URL}admin/companies`, [
      { id: companyId, name },
    ])
  }

  // 사용자 정보 수정
  export const updateUserInfo = async (
    userId: number,
    data: { name?: string; email?: string | null },
  ): Promise<Array<UserInfo>> => {
    return await AppAuth.Put<Array<UserInfo>>(`${API_URL}admin/users`, [{ id: userId, ...data }])
  }

  /**
   * 사용자 비밀번호 초기화
   */
  export const resetUserPassword = async (userId: number): Promise<{ successCount: number }> => {
    return await AppAuth.Put<{ successCount: number }>(`${API_URL}admin/users/reset-password`, [
      { id: userId },
    ])
  }

  /**
   * 회사 삭제
   */
  export const deleteCompany = async (companyId: number): Promise<{ successCount: number }> => {
    return await AppAuth.Delete<{ successCount: number }>(`${API_URL}admin/companies`, [
      { id: companyId },
    ])
  }

  /**
   * 사용자 삭제
   */
  export const deleteUser = async (userId: number): Promise<{ successCount: number }> => {
    return await AppAuth.Delete<{ successCount: number }>(`${API_URL}admin/users`, [{ id: userId }])
  }

  /**
   * 관리자 권한 확인
   */
  export const isAdmin = async (): Promise<{ isAdmin: boolean }> => {
    return await AppAuth.Get<{ isAdmin: boolean }>(`${API_URL}me/is-admin`)
  }

  /**
   * 비밀번호 접미사 조회 (관리자 전용)
   */
  export const getPasswordSuffix = async (): Promise<{ suffix: string }> => {
    return await AppAuth.Get<{ suffix: string }>(`${API_URL}admin/password-suffix`)
  }

  /**
   * 내 비밀번호 변경
   */
  export const updateMyPassword = async (
    currentPassword: string,
    newPassword: string,
  ): Promise<void> => {
    return await AppAuth.Put<void>(`${API_URL}me/password`, [
      {
        current_password: currentPassword,
        new_password: newPassword,
      },
    ])
  }

  // 관리자 대시보드 통계 조회
  export const getDashboardStats = async (params?: {
    type?: 'ind' | 'biz' | 'all'
    startDate?: string
    endDate?: string
    company_id?: number
    granularity?: 'day' | 'month'
  }): Promise<{
    total: {
      companies: { all: number; biz: number; ind: number }
      tasks: { all: number; biz: number; ind: number }
      defects: { all: number; biz: number; ind: number }
      defect_photos: { all: number; biz: number; ind: number }
      active_companies?: { all: number; biz: number; ind: number }
      usage_time?: number
    }
    monthly: Array<{
      month: string
      companies: { all: number; biz: number; ind: number }
      tasks: { all: number; biz: number; ind: number }
      defects: { all: number; biz: number; ind: number }
      defect_photos: { all: number; biz: number; ind: number }
      active_companies: { all: number; biz: number; ind: number }
      usage_time?: number
    }>
  }> => {
    const query = new URLSearchParams()
    if (params?.type) query.append('type', params.type)
    if (params?.startDate) query.append('startDate', params.startDate)
    if (params?.endDate) query.append('endDate', params.endDate)
    if (params?.company_id) query.append('company_id', String(params.company_id))
    if (params?.granularity) query.append('granularity', params.granularity)

    const queryString = query.toString()
    return await AppAuth.Get(`${API_URL}admin/dashboard${queryString ? `?${queryString}` : ''}`)
  }

  // 관리자 사용자 목록 조회
  export const getUsers = async (params?: {
    type?: 'all' | 'biz' | 'ind'
    page?: number
    limit?: number
    startDate?: string
    endDate?: string
    search?: string
    sort?: string
    company_id?: number | number[]
  }): Promise<
    Array<{
      id: number
      login_id: string
      name: string
      email: string | null
      company_id: number
      created_at: string
      updated_at: string
      company_name: string
      company_type: 'BIZ' | 'IND'
      task_count: number
      defect_count: number
      defect_photo_count: number
      usage_time: number
      last_access_at: string | null
    }>
  > => {
    const query = new URLSearchParams()
    if (params?.type) query.append('type', params.type)
    if (params?.page) query.append('page', String(params.page))
    if (params?.limit) query.append('limit', String(params.limit))
    if (params?.startDate) query.append('startDate', params.startDate)
    if (params?.endDate) query.append('endDate', params.endDate)
    if (params?.search) query.append('search', params.search)
    if (params?.sort) query.append('sort', params.sort)
    if (params?.company_id) {
      if (Array.isArray(params.company_id)) {
        query.append('company_id', JSON.stringify(params.company_id))
      } else {
        query.append('company_id', String(params.company_id))
      }
    }

    const queryString = query.toString()
    return await AppAuth.Get(`${API_URL}admin/users${queryString ? `?${queryString}` : ''}`)
  }

  // 관리자 사용자 총 개수 조회
  export const getUsersCount = async (params?: {
    type?: 'all' | 'biz' | 'ind'
    search?: string
    company_id?: number | number[]
  }): Promise<{ totalCount: number }> => {
    const query = new URLSearchParams()
    if (params?.type) query.append('type', params.type)
    if (params?.search) query.append('search', params.search)
    if (params?.company_id) {
      if (Array.isArray(params.company_id)) {
        query.append('company_id', JSON.stringify(params.company_id))
      } else {
        query.append('company_id', String(params.company_id))
      }
    }

    const queryString = query.toString()
    return await AppAuth.Get(`${API_URL}admin/users/count${queryString ? `?${queryString}` : ''}`)
  }

  /** 일반 유저용: 본인 문의 조회 (files, comments 포함) */
  export const getQnaList = async (params?: {
    page?: number
    limit?: number
    order?: string
    id?: number | number[]
  }): Promise<Qna[]> => {
    const query = new URLSearchParams()
    if (params?.page) query.append('page', String(params.page))
    if (params?.limit) query.append('limit', String(params.limit))
    if (params?.order) query.append('order', params.order)
    if (params?.id) {
      query.append('id', Array.isArray(params.id) ? JSON.stringify(params.id) : String(params.id))
    }
    const qs = query.toString()
    return await AppAuth.Get<Qna[]>(`${API_URL}qnas${qs ? `?${qs}` : ''}`)
  }

  /** admin용: 전체 문의 조회 (files, comments 포함) */
  export const getAdminQnaList = async (params?: {
    page?: number
    limit?: number
    order?: string
    id?: number | number[]
    company_id?: number | number[]
    created_user_id?: number
  }): Promise<Qna[]> => {
    const query = new URLSearchParams()
    if (params?.page) query.append('page', String(params.page))
    if (params?.limit) query.append('limit', String(params.limit))
    if (params?.order) query.append('order', params.order)
    if (params?.id) {
      query.append('id', Array.isArray(params.id) ? JSON.stringify(params.id) : String(params.id))
    }
    if (params?.company_id) {
      query.append(
        'company_id',
        Array.isArray(params.company_id)
          ? JSON.stringify(params.company_id)
          : String(params.company_id),
      )
    }
    if (params?.created_user_id) {
      query.append('created_user_id', String(params.created_user_id))
    }
    const qs = query.toString()
    return await AppAuth.Get<Qna[]>(`${API_URL}admin/qnas${qs ? `?${qs}` : ''}`)
  }

  // QnA 생성 (일반 유저)
  export const createQna = async (
    data: { content: string; contact?: string }[],
  ): Promise<Qna[]> => {
    return await AppAuth.Post<Qna[]>(`${API_URL}qnas`, data)
  }

  // QnA 삭제 (일반 유저: 본인 글)
  export const deleteQna = async (data: { id: number }[]): Promise<{ successCount: number }> => {
    return await AppAuth.Delete<{ successCount: number }>(`${API_URL}qnas`, data)
  }

  // QnA 삭제 (admin)
  export const deleteAdminQna = async (
    data: { id: number }[],
  ): Promise<{ successCount: number }> => {
    return await AppAuth.Delete<{ successCount: number }>(`${API_URL}admin/qnas`, data)
  }

  // QnA 댓글 생성 (일반 유저: 본인 글에만)
  export const createQnaComment = async (
    data: { qna_id: number; content: string }[],
  ): Promise<QnaComment[]> => {
    return await AppAuth.Post<QnaComment[]>(`${API_URL}qna-comments`, data)
  }

  // QnA 댓글 생성 (admin: 모든 글에)
  export const createAdminQnaComment = async (
    data: { qna_id: number; content: string }[],
  ): Promise<QnaComment[]> => {
    return await AppAuth.Post<QnaComment[]>(`${API_URL}admin/qna-comments`, data)
  }

  // QnA 파일 생성 (일반 유저: 본인 글에만)
  export const createQnaFiles = async (
    data: { qna_id: number; url: string; file_name: string }[],
  ): Promise<QnaFile[]> => {
    return await AppAuth.Post<QnaFile[]>(`${API_URL}qna-files`, data)
  }

  // QnA 파일 삭제 (일반 유저: 본인 파일)
  export const deleteQnaFiles = async (
    data: { id: number }[],
  ): Promise<{ successCount: number }> => {
    return await AppAuth.Delete<{ successCount: number }>(`${API_URL}qna-files`, data)
  }

  // ─── 공지사항 ───

  // 공지사항 목록 조회 (일반 유저)
  export const getAnnouncementList = async (params?: {
    page?: number
    limit?: number
    order?: string
    id?: number | number[]
  }): Promise<Announcement[]> => {
    const query = new URLSearchParams()
    if (params?.page) query.append('page', String(params.page))
    if (params?.limit) query.append('limit', String(params.limit))
    if (params?.order) query.append('order', params.order)
    if (params?.id) {
      query.append('id', Array.isArray(params.id) ? JSON.stringify(params.id) : String(params.id))
    }
    const qs = query.toString()
    return await AppAuth.Get<Announcement[]>(`${API_URL}announcements${qs ? `?${qs}` : ''}`)
  }

  // 메인 노출 공지사항 조회
  export const getPinnedAnnouncements = async (): Promise<Announcement[]> => {
    return await AppAuth.Get<Announcement[]>(`${API_URL}announcements/pinned`)
  }

  // admin: 공지사항 목록 조회 (상세)
  export const getAdminAnnouncementList = async (params?: {
    page?: number
    limit?: number
    order?: string
    id?: number | number[]
    is_pinned?: boolean
  }): Promise<Announcement[]> => {
    const query = new URLSearchParams()
    if (params?.page) query.append('page', String(params.page))
    if (params?.limit) query.append('limit', String(params.limit))
    if (params?.order) query.append('order', params.order)
    if (params?.id) {
      query.append('id', Array.isArray(params.id) ? JSON.stringify(params.id) : String(params.id))
    }
    if (params?.is_pinned !== undefined) {
      query.append('is_pinned', String(params.is_pinned))
    }
    const qs = query.toString()
    return await AppAuth.Get<Announcement[]>(`${API_URL}admin/announcements${qs ? `?${qs}` : ''}`)
  }

  // admin: 공지사항 생성
  export const createAnnouncement = async (
    data: { title: string; content: string; is_pinned?: boolean }[],
  ): Promise<Announcement[]> => {
    return await AppAuth.Post<Announcement[]>(`${API_URL}admin/announcements`, data)
  }

  // admin: 공지사항 수정
  export const updateAnnouncement = async (
    data: { id: number; title?: string; content?: string; is_pinned?: boolean }[],
  ): Promise<Announcement[]> => {
    return await AppAuth.Put<Announcement[]>(`${API_URL}admin/announcements`, data)
  }

  // admin: 공지사항 삭제
  export const deleteAnnouncement = async (
    data: { id: number }[],
  ): Promise<{ successCount: number }> => {
    return await AppAuth.Delete<{ successCount: number }>(`${API_URL}admin/announcements`, data)
  }

  // admin: 공지사항 사진 추가
  export const createAnnouncementPhotos = async (
    data: { announcement_id: number; url: string; file_name: string }[],
  ): Promise<AnnouncementPhoto[]> => {
    return await AppAuth.Post<AnnouncementPhoto[]>(`${API_URL}admin/announcement-photos`, data)
  }

  // admin: 공지사항 사진 삭제
  export const deleteAnnouncementPhotos = async (
    data: { id: number }[],
  ): Promise<{ successCount: number }> => {
    return await AppAuth.Delete<{ successCount: number }>(
      `${API_URL}admin/announcement-photos`,
      data,
    )
  }

  /* ---------------- 품질점검 ---------------- */

  // 품질점검: 체크리스트 세트
  export const getQualityChecklistSetList = async (): Promise<QualityChecklistSet[]> => {
    return await AppAuth.Get<QualityChecklistSet[]>(`${API_URL}quality-checklist-sets`)
  }

  export const addQualityChecklistSets = async (
    data: Partial<QualityChecklistSet>[],
  ): Promise<QualityChecklistSet[]> => {
    return await AppAuth.Post<QualityChecklistSet[]>(`${API_URL}quality-checklist-sets`, data)
  }

  export const updateQualityChecklistSets = async (
    data: Partial<QualityChecklistSet>[],
  ): Promise<QualityChecklistSet[]> => {
    return await AppAuth.Put<QualityChecklistSet[]>(`${API_URL}quality-checklist-sets`, data)
  }

  export const deleteQualityChecklistSets = async (ids: { id: number }[]): Promise<void> => {
    return await AppAuth.Delete(`${API_URL}quality-checklist-sets`, ids)
  }

  // 품질점검: 체크리스트 구역 (masterOnly: 마스터 행만)
  export const getQualityChecklistZones = async (params: {
    set_id?: number
    task_id?: number
    master?: boolean
  }): Promise<QualityChecklistZone[]> => {
    const query = toQueryString(params)
    return await AppAuth.Get<QualityChecklistZone[]>(`${API_URL}quality-checklist-zones${query}`)
  }

  export const addQualityChecklistZones = async (
    data: Partial<QualityChecklistZone>[],
  ): Promise<QualityChecklistZone[]> => {
    return await AppAuth.Post<QualityChecklistZone[]>(`${API_URL}quality-checklist-zones`, data)
  }

  export const updateQualityChecklistZones = async (
    data: Partial<QualityChecklistZone>[],
  ): Promise<QualityChecklistZone[]> => {
    return await AppAuth.Put<QualityChecklistZone[]>(`${API_URL}quality-checklist-zones`, data)
  }

  export const deleteQualityChecklistZones = async (ids: { id: number }[]): Promise<void> => {
    return await AppAuth.Delete(`${API_URL}quality-checklist-zones`, ids)
  }

  // 품질점검: 체크리스트 항목
  export const getQualityChecklistItems = async (params: {
    set_id?: number
    zone_id?: number
    task_id?: number
    master?: boolean
  }): Promise<QualityChecklistItem[]> => {
    const query = toQueryString(params)
    return await AppAuth.Get<QualityChecklistItem[]>(`${API_URL}quality-checklist-items${query}`)
  }

  export const addQualityChecklistItems = async (
    data: Partial<QualityChecklistItem>[],
  ): Promise<QualityChecklistItem[]> => {
    return await AppAuth.Post<QualityChecklistItem[]>(`${API_URL}quality-checklist-items`, data)
  }

  export const updateQualityChecklistItems = async (
    data: Partial<QualityChecklistItem>[],
  ): Promise<QualityChecklistItem[]> => {
    return await AppAuth.Put<QualityChecklistItem[]>(`${API_URL}quality-checklist-items`, data)
  }

  export const deleteQualityChecklistItems = async (ids: { id: number }[]): Promise<void> => {
    return await AppAuth.Delete(`${API_URL}quality-checklist-items`, ids)
  }

  // 품질점검: 과업에 체크리스트 스냅샷 복사
  export const snapshotQualityChecklist = async (
    taskId: number,
    setId: number,
  ): Promise<{ zones: QualityChecklistZone[]; items: QualityChecklistItem[] }> => {
    return await AppAuth.Post<{ zones: QualityChecklistZone[]; items: QualityChecklistItem[] }>(
      `${API_URL}quality-checklist-items/snapshot`,
      { task_id: taskId, set_id: setId },
    )
  }

  // 품질점검: 과업 배정
  export const getQualityTaskAssignments = async (params: {
    task_id?: number
    user_id?: number
  }): Promise<QualityTaskAssignment[]> => {
    const query = toQueryString(params)
    return await AppAuth.Get<QualityTaskAssignment[]>(`${API_URL}quality-task-assignments${query}`)
  }

  export const addQualityTaskAssignments = async (
    data: Partial<QualityTaskAssignment>[],
  ): Promise<QualityTaskAssignment[]> => {
    return await AppAuth.Post<QualityTaskAssignment[]>(`${API_URL}quality-task-assignments`, data)
  }

  export const updateQualityTaskAssignments = async (
    data: Partial<QualityTaskAssignment>[],
  ): Promise<QualityTaskAssignment[]> => {
    return await AppAuth.Put<QualityTaskAssignment[]>(`${API_URL}quality-task-assignments`, data)
  }

  export const deleteQualityTaskAssignments = async (ids: { id: number }[]): Promise<void> => {
    return await AppAuth.Delete(`${API_URL}quality-task-assignments`, ids)
  }

  // 품질점검: 점검 세션
  export const getQualityInspectionListByTaskId = async (
    taskId: number,
  ): Promise<QualityInspection[]> => {
    return await AppAuth.Get<QualityInspection[]>(`${API_URL}quality-inspections?task_id=${taskId}`)
  }

  export const addQualityInspections = async (
    data: Partial<QualityInspection>[],
  ): Promise<QualityInspection[]> => {
    return await AppAuth.Post<QualityInspection[]>(`${API_URL}quality-inspections`, data)
  }

  export const updateQualityInspections = async (
    data: Partial<QualityInspection>[],
  ): Promise<QualityInspection[]> => {
    return await AppAuth.Put<QualityInspection[]>(`${API_URL}quality-inspections`, data)
  }

  export const deleteQualityInspections = async (ids: { id: number }[]): Promise<void> => {
    return await AppAuth.Delete(`${API_URL}quality-inspections`, ids)
  }

  // 품질점검: 세션 검수 (총괄 전용, status 미지정 시 REVIEWED)
  export const reviewQualityInspections = async (
    data: { id: number; status?: QualityInspectionStatus }[],
  ): Promise<QualityInspection[]> => {
    return await AppAuth.Post<QualityInspection[]>(`${API_URL}quality-inspections/review`, data)
  }

  // 품질점검: 점검 결과
  export const getQualityResultListByTaskId = async (
    taskId: number,
  ): Promise<QualityInspectionResult[]> => {
    return await AppAuth.Get<QualityInspectionResult[]>(
      `${API_URL}quality-inspection-results?task_id=${taskId}`,
    )
  }

  export const addQualityResults = async (
    data: Partial<QualityInspectionResult>[],
  ): Promise<QualityInspectionResult[]> => {
    return await AppAuth.Post<QualityInspectionResult[]>(
      `${API_URL}quality-inspection-results`,
      data,
    )
  }

  export const updateQualityResults = async (
    data: Partial<QualityInspectionResult>[],
  ): Promise<QualityInspectionResult[]> => {
    return await AppAuth.Put<QualityInspectionResult[]>(
      `${API_URL}quality-inspection-results`,
      data,
    )
  }

  export const deleteQualityResults = async (ids: { id: number }[]): Promise<void> => {
    return await AppAuth.Delete(`${API_URL}quality-inspection-results`, ids)
  }

  // 품질점검: 결과 사진
  export const getQualityResultPhotoListByTaskId = async (
    taskId: number,
  ): Promise<QualityResultPhoto[]> => {
    return await AppAuth.Get<QualityResultPhoto[]>(
      `${API_URL}quality-result-photos?task_id=${taskId}`,
    )
  }

  export const addQualityResultPhotos = async (
    data: Partial<QualityResultPhoto>[],
  ): Promise<QualityResultPhoto[]> => {
    return await AppAuth.Post<QualityResultPhoto[]>(`${API_URL}quality-result-photos`, data)
  }

  export const updateQualityResultPhotos = async (
    data: Partial<QualityResultPhoto>[],
  ): Promise<QualityResultPhoto[]> => {
    return await AppAuth.Put<QualityResultPhoto[]>(`${API_URL}quality-result-photos`, data)
  }

  export const deleteQualityResultPhotos = async (ids: { id: number }[]): Promise<void> => {
    return await AppAuth.Delete(`${API_URL}quality-result-photos`, ids)
  }

  // 품질점검: 협력업체
  export const getQualityPartnerListByTaskId = async (
    taskId: number,
  ): Promise<QualityPartner[]> => {
    return await AppAuth.Get<QualityPartner[]>(`${API_URL}quality-partners?task_id=${taskId}`)
  }

  export const addQualityPartners = async (
    data: Partial<QualityPartner>[],
  ): Promise<QualityPartner[]> => {
    return await AppAuth.Post<QualityPartner[]>(`${API_URL}quality-partners`, data)
  }

  export const updateQualityPartners = async (
    data: Partial<QualityPartner>[],
  ): Promise<QualityPartner[]> => {
    return await AppAuth.Put<QualityPartner[]>(`${API_URL}quality-partners`, data)
  }

  export const deleteQualityPartners = async (ids: { id: number }[]): Promise<void> => {
    return await AppAuth.Delete(`${API_URL}quality-partners`, ids)
  }

  // 품질점검 총괄: 자기 회사 계정 관리
  export const getManagerUserList = async (): Promise<UserInfo[]> => {
    return await AppAuth.Get<UserInfo[]>(`${API_URL}manager/users`)
  }

  export const addManagerUsers = async (
    data: { login_id: string; name: string; email?: string; password?: string; role?: UserRole }[],
  ): Promise<UserInfo[]> => {
    return await AppAuth.Post<UserInfo[]>(`${API_URL}manager/users`, data)
  }

  export const updateManagerUsers = async (
    data: { id: number; name?: string; email?: string; role?: UserRole }[],
  ): Promise<UserInfo[]> => {
    return await AppAuth.Put<UserInfo[]>(`${API_URL}manager/users`, data)
  }

  export const resetManagerUserPasswords = async (
    ids: { id: number }[],
  ): Promise<{ successCount: number }> => {
    return await AppAuth.Put<{ successCount: number }>(
      `${API_URL}manager/users/reset-password`,
      ids,
    )
  }

  export const deleteManagerUsers = async (
    ids: { id: number }[],
  ): Promise<{ successCount: number }> => {
    return await AppAuth.Delete<{ successCount: number }>(`${API_URL}manager/users`, ids)
  }

  // 품질점검 총괄: 자동 생성 비밀번호 접미사 조회 (초기 비밀번호 안내용)
  export const getManagerPasswordSuffix = async (): Promise<{ suffix: string }> => {
    return await AppAuth.Get<{ suffix: string }>(`${API_URL}manager/password-suffix`)
  }

  // ──────────────────────────────────────────────
  // 보고서 PDF → 전차 결함 등록 (report import) — hck2 전용, 서버 2중 게이트
  // ──────────────────────────────────────────────

  /**
   * job 생성 (PDF 를 GCS 에 올린 뒤 호출, status=QUEUED)
   * - 같은 과업에 진행 중인 job 이 있으면 409 (ApiError.statusCode === 409)
   */
  export const createReportImport = async (payload: {
    task_id: number
    source_file_url: string
    source_file_name: string
    source_file_size: number
  }): Promise<ReportImport> => {
    return await AppAuth.Post<ReportImport>(`${API_URL}report-imports`, payload, {
      timeoutMs: 20_000,
    })
  }

  /**
   * 과업의 job 목록 (최근순). 조사위치도 진입 시 진행 중/검토 중 job 복원용
   */
  export const getReportImports = async (
    taskId: number,
    status?: string,
  ): Promise<ReportImport[]> => {
    const query = toQueryString({ task_id: taskId, ...(status ? { status } : {}) })
    return await AppAuth.Get<ReportImport[]>(`${API_URL}report-imports${query}`, {
      timeoutMs: 20_000,
    })
  }

  /**
   * job 상세 (items 는 REVIEW 이후에만 포함, facilities 는 과업 시설물/층 트리). 3초 폴링 대상
   */
  export const getReportImport = async (id: number): Promise<ReportImportDetail> => {
    return await AppAuth.Get<ReportImportDetail>(`${API_URL}report-imports/${id}`, {
      timeoutMs: 20_000,
    })
  }

  /**
   * 섹션 매핑 확정 + 항목 제외 저장 (REVIEW 에서만)
   * - excluded_item_ids 는 전체 집합 (목록에 없는 항목은 포함으로 되돌아감)
   */
  export const updateReportImportMapping = async (
    id: number,
    payload: {
      section_mappings?: {
        key: string
        confirmed: { facility_id: number; floorplan_id: number } | null
        skip: boolean
      }[]
      excluded_item_ids?: number[]
    },
  ): Promise<ReportImportDetail> => {
    return await AppAuth.Put<ReportImportDetail>(
      `${API_URL}report-imports/${id}/mapping`,
      payload,
      { timeoutMs: 30_000 },
    )
  }

  /**
   * 커밋: NO_SHAPE 전차 결함 + 전차 사진 생성 (REVIEW 에서만). 사진 복사가 포함되어 길다
   */
  export const commitReportImport = async (id: number): Promise<ReportImportCommitResult> => {
    return await AppAuth.Post<ReportImportCommitResult>(
      `${API_URL}report-imports/${id}/commit`,
      {},
      { timeoutMs: 120_000 },
    )
  }

  /**
   * 취소: QUEUED/REVIEW/FAILED → CANCELED, PROCESSING 은 워커가 다음 하트비트에서 중단
   */
  export const cancelReportImport = async (id: number): Promise<ReportImport> => {
    return await AppAuth.Post<ReportImport>(
      `${API_URL}report-imports/${id}/cancel`,
      {},
      { timeoutMs: 20_000 },
    )
  }
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace PublicAPI {
  export const getPublicData = async (key: string): Promise<PublicData> => {
    try {
      // Public API는 인증 없이 호출
      const response = await axios.get(`${API_URL}public/data?key=${key}`)

      if (response.data.statusCode !== 200) {
        throw new Error(`[PUBLIC API ERROR] ${response.data.message}`)
      }

      return response.data.data
    } catch (ex: unknown) {
      if (ex instanceof Error) {
        ex.message = `[PUBLIC API ERROR] ${ex.message}`
        throw ex
      } else {
        throw new Error('[PUBLIC API ERROR] 알 수 없는 오류')
      }
    }
  }
  export const getPublicAppVersions = async (currentVersion?: string): Promise<AppVersion[]> => {
    try {
      // Public API는 인증 없이 호출
      // 현재 앱의 빌드타입에 따라 분기
      const buildType: 'dev' | 'production' = _.IS_PRODUCTION ? 'production' : 'dev'
      const params = new URLSearchParams({ build_type: buildType })
      if (currentVersion) params.append('currentVersion', currentVersion)

      // 주의) 앱 버전관련은 무조건 실서버 테이블을 참조함
      // 폴링성 요청이므로 회색지대 네트워크에서 무한 대기하지 않도록 타임아웃 적용
      const response = await axios.get(`${API_URL}public/app-versions?${params.toString()}`, {
        timeout: 10_000,
      })

      if (response.data.statusCode !== 200) {
        throw new Error(`[PUBLIC API ERROR] ${response.data.message}`)
      }

      return response.data.data
    } catch (ex: unknown) {
      if (ex instanceof Error) {
        ex.message = `[PUBLIC API ERROR] ${ex.message}`
        throw ex
      } else {
        throw new Error('[PUBLIC API ERROR] 알 수 없는 오류')
      }
    }
  }
  export const getShapeCommentsByShapeId = async (
    defectId: number,
  ): Promise<{
    defect_comment: ShapeComment[]
    defect_comment_photo: ShapeCommentPhoto[]
  }> => {
    try {
      // Public API는 인증 없이 호출
      const response = await axios.get(`${API_URL}public/defect-comment?defect_id=${defectId}`)

      if (response.data.statusCode !== 200) {
        throw new Error(`[PUBLIC API ERROR] ${response.data.message}`)
      }

      return response.data.data
    } catch (ex: unknown) {
      if (ex instanceof Error) {
        ex.message = `[PUBLIC API ERROR] ${ex.message}`
        throw ex
      } else {
        throw new Error('[PUBLIC API ERROR] 알 수 없는 오류')
      }
    }
  }

  export const addShapeComment = async (data: Partial<ShapeComment>): Promise<ShapeComment> => {
    try {
      // Public API는 인증 없이 호출
      const response = await axios.post(`${API_URL}public/defect-comment`, data)
      if (response.data.statusCode !== 200) {
        throw new Error(`[PUBLIC API ERROR] ${response.data.message}`)
      }

      return response.data.data
    } catch (ex: unknown) {
      if (ex instanceof Error) {
        ex.message = `[PUBLIC API ERROR] ${ex.message}`
        throw ex
      } else {
        throw new Error('[PUBLIC API ERROR] 알 수 없는 오류')
      }
    }
  }

  export const addShapeCommentPhoto = async (
    data: Partial<ShapeCommentPhoto>,
  ): Promise<ShapeCommentPhoto> => {
    try {
      // Public API는 인증 없이 호출
      const response = await axios.post(`${API_URL}public/defect-comment-photo`, data)

      if (response.data.statusCode !== 200) {
        throw new Error(`[PUBLIC API ERROR] ${response.data.message}`)
      }

      return response.data.data
    } catch (ex: unknown) {
      if (ex instanceof Error) {
        ex.message = `[PUBLIC API ERROR] ${ex.message}`
        throw ex
      } else {
        throw new Error('[PUBLIC API ERROR] 알 수 없는 오류')
      }
    }
  }

  export const updateShapeCommentPhoto = async (
    data: Partial<ShapeCommentPhoto>[],
  ): Promise<void> => {
    try {
      // Public API는 인증 없이 호출
      const response = await axios.put(`${API_URL}public/defect-comment-photo`, data)

      if (response.data.statusCode !== 200) {
        throw new Error(`[PUBLIC API ERROR] ${response.data.message}`)
      }

      return response.data.data
    } catch (ex: unknown) {
      if (ex instanceof Error) {
        ex.message = `[PUBLIC API ERROR] ${ex.message}`
        throw ex
      } else {
        throw new Error('[PUBLIC API ERROR] 알 수 없는 오류')
      }
    }
  }

  export const deleteShapeCommentPhoto = async (ids: { id: number }[]): Promise<void> => {
    try {
      // Public API는 인증 없이 호출
      const response = await axios.delete(`${API_URL}public/defect-comment-photos`, { data: ids })

      if (response.data.statusCode !== 200) {
        throw new Error(`[PUBLIC API ERROR] ${response.data.message}`)
      }

      return response.data.data
    } catch (ex: unknown) {
      if (ex instanceof Error) {
        ex.message = `[PUBLIC API ERROR] ${ex.message}`
        throw ex
      } else {
        throw new Error('[PUBLIC API ERROR] 알 수 없는 오류')
      }
    }
  }
}
